import React from 'react'
import styled, { css } from 'styled-components'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'

import isNaN from 'lodash/isNaN'
import map from 'ramda/src/map'

import { ppsSelector } from '../../../selectors'
import { THB_FULL, THB_SHORT } from '../../../constants'

import Typography from '../../../lib/typography'
import { parseFinancial, financialFormatter } from '../../../lib/utils'

import { createTableCard } from '../../../components/project-profit-sheet/TableCard'
import IndicatorCost, * as IndicatorCostLayout from '../../../components/project-profit-sheet/IndicatorCost'

import { selectMode, determineColor, determineStatusColor } from '../model'
import { ABOVE_CURRENT_PRICE, ABOVE_INITIAL_PLAN } from '../constants'

const fallbackNaN = (number) => (isNaN(number) ? '0.00' : number)

const BaseCard = createTableCard(css`
  grid-template-columns: repeat(7, calc(100% / 7));
`)

const Card = {
  ...BaseCard,
  Container: styled(BaseCard.Container)`
    height: 70px;
    padding-bottom: 12px;
    align-items: end;

    ${IndicatorCostLayout.Root} {
      width: 95%;
      margin-left: 8px;
      grid-column: span 3;
    }
  `,
}

/**
 * @param {object} props
 * @param {object} props.profitData
 * @param {string} props.display
 */
function PriceCostContainer({ profitData, display }) {
  const mode = selectMode(display)
  const data = map(parseFinancial, {
    forecast: profitData[`$${mode}_price_cost_forecast`],
    actual: profitData[`$${mode}_price_cost_actual`],
    remaining: profitData[`$${mode}_price_cost_remaining`],
    initial: profitData[`$${mode}_price_cost_initial_plan`],
    contract: profitData['$price_cost_contract_price'],
    cr: profitData['$price_cost_change_request_price'],
    current: profitData['$price_cost_current_price'],
    gap: profitData[`$${mode}_price_cost_gap`],
  })
  const dataShow = map(financialFormatter, data)

  const bars = [
    {
      value: data.actual,
      color: '#5cb85c',
      label: 'Actual Cost',
      formatter: { full: THB_FULL },
    },
    {
      value: data.remaining,
      color: '#9be89b',
      label: 'Remaining Cost',
      formatter: { full: THB_FULL },
    },
  ]

  const markers = [
    {
      value: data.initial,
      color: '#404040',
      type: 'solid',
      label: 'Approved Plan Cost',
      formatter: { abbr: THB_SHORT, full: THB_FULL },
      trigger: ABOVE_INITIAL_PLAN,
      direction: 'right',
    },
    {
      value: data.current,
      color: '#707070',
      type: 'dotted',
      label: 'Current Price',
      formatter: { abbr: THB_SHORT, full: THB_FULL },
      trigger: ABOVE_CURRENT_PRICE,
      direction: 'left',
    },
  ]

  if (markers[0].value > markers[1].value) {
    [markers[0].direction, markers[1].direction] = [markers[1].direction, markers[0].direction]
  }

  return (
    <Card.Root topic="Price & Cost Summary">
      <Card.Heading>
        <Typography.Data>Forecast Cost</Typography.Data>
        <Typography.Data>Approved Plan Cost</Typography.Data>
        <Typography.Data style={ { paddingLeft: 8 } }>Progress</Typography.Data>
        <div />
        <div />
        <Typography.Data>CR</Typography.Data>
        <Typography.Data title="Forecast - Approved Plan Cost">
          Forecast - Approved Plan Cost
        </Typography.Data>
      </Card.Heading>
      <Card.Container>
        <Typography.Data disc={ determineStatusColor(data) }>
          ฿ {fallbackNaN(dataShow.forecast) || '0.00'}
        </Typography.Data>
        <Typography.Data disc="black" weight="normal">
          ฿ {fallbackNaN(dataShow.initial) || '0.00'}
        </Typography.Data>
        <IndicatorCost bars={ bars } markers={ markers } />
        <Typography.Data weight="normal">฿ {fallbackNaN(dataShow.cr) || '0.00'}</Typography.Data>
        <Typography.Data weight="normal" color={ determineColor(data.gap) }>
          ฿ {fallbackNaN(dataShow.gap) || '0.00'}
        </Typography.Data>
      </Card.Container>
    </Card.Root>
  )
}

const mapStateToProps = createStructuredSelector({
  profitData: ppsSelector.profitData,
  display: ppsSelector.display,
})

export default connect(mapStateToProps)(PriceCostContainer)



// WEBPACK FOOTER //
// ./src/pages/project-profit-sheet/containers/PriceCostContainer.js